import {
  AUTHORED_ELEVATORS,
  authoredElevatorWorldStop,
  type AuthoredElevatorDefinition,
  type AuthoredElevatorId,
  type AuthoredElevatorStop,
  type AuthoredElevatorStopId,
} from "../../config/elevators.ts";
import type { PlayerId } from "../../core/ids.ts";
import { clamp01 } from "../../core/math.ts";
import type { InputIntent } from "../../input/intents.ts";
import type { GameState } from "../state.ts";

export interface IdleElevatorRideState {
  readonly phase: "idle";
  readonly rideCount: number;
}

export interface TravellingElevatorRideState {
  readonly phase: "travelling";
  readonly elevatorId: AuthoredElevatorId;
  readonly fromStopId: AuthoredElevatorStopId;
  readonly toStopId: AuthoredElevatorStopId;
  readonly elapsedSeconds: number;
  readonly durationSeconds: number;
  readonly originPosition: Readonly<{ x: number; y: number; z: number }>;
  readonly destinationPosition: Readonly<{ x: number; y: number; z: number }>;
  readonly destinationFacingYaw: number;
  readonly transferred: boolean;
  readonly rideCount: number;
}

export type ElevatorRideState =
  | IdleElevatorRideState
  | TravellingElevatorRideState;

export interface ElevatorRidePresentation {
  readonly elevatorLabel: string;
  readonly fromLabel: string;
  readonly fromLevelLabel: string;
  readonly toLabel: string;
  readonly toLevelLabel: string;
  readonly progress01: number;
}

export interface ElevatorPlayerOverride {
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly facingYaw: number;
  readonly surfaceId: string;
}

export interface ElevatorRideStepResult {
  readonly state: ElevatorRideState;
  readonly interactionConsumed: boolean;
  readonly transition: "departed" | "transferred" | "arrived" | null;
  readonly playerOverride: ElevatorPlayerOverride | null;
  readonly presentation: ElevatorRidePresentation | null;
}

interface NearbyStop {
  readonly elevator: AuthoredElevatorDefinition;
  readonly stop: AuthoredElevatorStop;
  readonly distanceM: number;
}

export function createElevatorRideState(): ElevatorRideState {
  return { phase: "idle", rideCount: 0 };
}

function definitionById(id: AuthoredElevatorId) {
  return AUTHORED_ELEVATORS.find((elevator) => elevator.id === id) ?? null;
}

function stopById(
  elevator: Readonly<AuthoredElevatorDefinition>,
  id: AuthoredElevatorStopId,
) {
  return elevator.stops.find((stop) => stop.id === id) ?? null;
}

function nearestElevatorStop(
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
): NearbyStop | null {
  let nearest: NearbyStop | null = null;
  for (const elevator of AUTHORED_ELEVATORS) {
    for (const stop of elevator.stops) {
      const world = authoredElevatorWorldStop(elevator, stop);
      const planarDistanceM = Math.hypot(
        playerPosition.x - world.x,
        playerPosition.z - world.z,
      );
      if (
        planarDistanceM > stop.interactionRadiusM ||
        Math.abs(playerPosition.y - world.y) > stop.interactionHeightM
      ) {
        continue;
      }
      if (nearest === null || planarDistanceM < nearest.distanceM) {
        nearest = { elevator, stop, distanceM: planarDistanceM };
      }
    }
  }
  return nearest;
}

function otherStop(
  elevator: Readonly<AuthoredElevatorDefinition>,
  fromStopId: AuthoredElevatorStopId,
): AuthoredElevatorStop | null {
  return elevator.stops.find((stop) => stop.id !== fromStopId) ?? null;
}

function presentationFor(
  state: Readonly<TravellingElevatorRideState>,
): ElevatorRidePresentation | null {
  const elevator = definitionById(state.elevatorId);
  if (elevator === null) return null;
  const from = stopById(elevator, state.fromStopId);
  const to = stopById(elevator, state.toStopId);
  if (from === null || to === null) return null;
  return {
    elevatorLabel: elevator.label,
    fromLabel: from.label,
    fromLevelLabel: from.levelLabel,
    toLabel: to.label,
    toLevelLabel: to.levelLabel,
    progress01: clamp01(state.elapsedSeconds / state.durationSeconds),
  };
}

export function elevatorRidePresentation(
  state: Readonly<ElevatorRideState>,
): ElevatorRidePresentation | null {
  return state.phase === "travelling" ? presentationFor(state) : null;
}

function overrideFor(
  state: Readonly<TravellingElevatorRideState>,
  atDestination: boolean,
): ElevatorPlayerOverride {
  return {
    position: {
      ...(atDestination ? state.destinationPosition : state.originPosition),
    },
    facingYaw: state.destinationFacingYaw,
    surfaceId: `elevator:${state.elevatorId}:${
      atDestination ? state.toStopId : state.fromStopId
    }`,
  };
}

export function stepElevatorRide(
  state: Readonly<ElevatorRideState>,
  intent: Readonly<InputIntent>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  playerFacingYaw: number,
  deltaSeconds: number,
): ElevatorRideStepResult {
  if (state.phase === "idle") {
    const nearby = nearestElevatorStop(playerPosition);
    if (!intent.interactPressed || nearby === null) {
      return {
        state,
        interactionConsumed: false,
        transition: null,
        playerOverride: null,
        presentation: null,
      };
    }
    const destination = otherStop(nearby.elevator, nearby.stop.id);
    if (destination === null) {
      return {
        state,
        interactionConsumed: false,
        transition: null,
        playerOverride: null,
        presentation: null,
      };
    }
    const worldDestination = authoredElevatorWorldStop(
      nearby.elevator,
      destination,
    );
    const next: TravellingElevatorRideState = {
      phase: "travelling",
      elevatorId: nearby.elevator.id,
      fromStopId: nearby.stop.id,
      toStopId: destination.id,
      elapsedSeconds: 0,
      durationSeconds: nearby.elevator.travelDurationSeconds,
      originPosition: {
        x: playerPosition.x,
        y: playerPosition.y,
        z: playerPosition.z,
      },
      destinationPosition: {
        x: worldDestination.x,
        y: worldDestination.y,
        z: worldDestination.z,
      },
      destinationFacingYaw: worldDestination.facingYaw,
      transferred: false,
      rideCount: state.rideCount,
    };
    return {
      state: next,
      interactionConsumed: true,
      transition: "departed",
      playerOverride: {
        position: {
          x: playerPosition.x,
          y: playerPosition.y,
          z: playerPosition.z,
        },
        facingYaw: playerFacingYaw,
        surfaceId: `elevator:${nearby.elevator.id}:${nearby.stop.id}`,
      },
      presentation: presentationFor(next),
    };
  }

  const elevator = definitionById(state.elevatorId);
  if (elevator === null) {
    return {
      state: createElevatorRideState(),
      interactionConsumed: true,
      transition: "arrived",
      playerOverride: overrideFor(state, state.transferred),
      presentation: null,
    };
  }
  const elapsedSeconds = Math.min(
    state.durationSeconds,
    state.elapsedSeconds + Math.max(0, deltaSeconds),
  );
  const progress01 = clamp01(elapsedSeconds / state.durationSeconds);
  const nowTransferred = progress01 >= elevator.transferProgress01;
  const transition = elapsedSeconds >= state.durationSeconds
    ? "arrived"
    : !state.transferred && nowTransferred
      ? "transferred"
      : null;
  const nextTravelling: TravellingElevatorRideState = {
    ...state,
    elapsedSeconds,
    transferred: state.transferred || nowTransferred,
  };
  const atDestination = nextTravelling.transferred;
  if (transition === "arrived") {
    return {
      state: { phase: "idle", rideCount: state.rideCount + 1 },
      interactionConsumed: true,
      transition,
      playerOverride: overrideFor(nextTravelling, true),
      presentation: null,
    };
  }
  return {
    state: nextTravelling,
    interactionConsumed: true,
    transition,
    playerOverride: overrideFor(nextTravelling, atDestination),
    presentation: presentationFor(nextTravelling),
  };
}

export function elevatorInteractionPrompt(
  state: Readonly<ElevatorRideState>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
): string | null {
  if (state.phase !== "idle") return null;
  return nearestElevatorStop(playerPosition)?.stop.prompt ?? null;
}

export function applyElevatorPlayerTick(
  state: Readonly<GameState>,
  playerId: PlayerId,
  tick: number,
  override: Readonly<ElevatorPlayerOverride>,
  deltaSeconds: number,
): GameState {
  const player = state.playersById[playerId];
  if (player === undefined) {
    throw new Error(`Player ${playerId} is missing from game state`);
  }
  return {
    ...state,
    tick,
    simulationTimeSeconds: state.simulationTimeSeconds + deltaSeconds,
    playersById: {
      ...state.playersById,
      [playerId]: {
        ...player,
        position: { ...override.position },
        velocity: { x: 0, y: 0, z: 0 },
        facingYaw: override.facingYaw,
        lookYaw: override.facingYaw,
        isSprinting: false,
        locomotion: {
          ...player.locomotion,
          mode: "grounded",
          grounded: true,
          groundNormal: { x: 0, y: 1, z: 0 },
          groundSlopeRadians: 0,
          surfaceId: override.surfaceId,
          coyoteTicksRemaining: 0,
          jumpBufferTicksRemaining: 0,
          landingResponseTicksRemaining: 0,
          mantleProgress: 0,
          footstepDistanceMetres: 0,
          lastStepHeightMetres: 0,
        },
      },
    },
  };
}
